import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Plus, Building2, Trash2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/admin/data-table';
import { CompanyModal } from '@/components/admin/company-modal';
import { CompanyDetailModal } from '@/components/admin/company-detail-modal';
import { DeleteCompanyModal } from '@/components/admin/DeleteCompanyModal';
import { useToast } from '@/hooks/use-toast';
import { useCompanyDeletion } from '@/hooks/use-company-deletion';
import { supabase } from '@/integrations/supabase/client';
import { useAuditLogger } from '@/hooks/use-audit-logger';

interface Company {
  id: string;
  name: string;
  slug: string;
  doc_cnpj?: string;
  email?: string;
  telefone?: string;
  timezone: string;
  status: string;
  created_at: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  branding?: {
    logo?: string;
    primary_color?: string;
    secondary_color?: string;
  };
  hasActiveTrial?: boolean;
  trialDaysRemaining?: number | null;
  console_customer_id?: string | null;
  console_synced_at?: string | null;
}

interface CompanyWithAdmin {
  name: string;
  slug: string;
  doc_cnpj?: string;
  timezone: string;
  status: string;
  admin_email: string;
  admin_name?: string;
  branding?: {
    logo?: string;
    primary_color?: string;
    secondary_color?: string;
  };
}

const CompaniesPage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { logAction } = useAuditLogger();
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const { deleteCompany, isDeleting } = useCompanyDeletion();

  const { data: companies, isLoading } = useQuery({
    queryKey: ['admin-companies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select(`
          *,
          company_system_plans(
            is_trial,
            trial_expires_at,
            status
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Process companies to add trial information
      const companiesWithTrialInfo = (data || []).map((company: any) => {
        const activeTrials = (company.company_system_plans || []).filter(
          (plan: any) => plan.is_trial && 
                        plan.status === 'active' && 
                        plan.trial_expires_at
        );
        
        let trialDaysRemaining = null;
        if (activeTrials.length > 0) {
          // Get the trial that expires soonest
          const nearestExpiry = activeTrials.reduce((earliest: any, plan: any) => {
            const expiryDate = new Date(plan.trial_expires_at);
            const earliestDate = new Date(earliest.trial_expires_at);
            return expiryDate < earliestDate ? plan : earliest;
          });
          
          const expiryDate = new Date(nearestExpiry.trial_expires_at);
          const today = new Date();
          const diffTime = expiryDate.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          trialDaysRemaining = diffDays;
        }
        
        const { company_system_plans, ...companyData } = company;
        return {
          ...companyData,
          hasActiveTrial: activeTrials.length > 0,
          trialDaysRemaining
        } as Company;
      });
      
      return companiesWithTrialInfo;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CompanyWithAdmin) => {
      console.log('Creating company with admin:', data);
      const { data: result, error } = await supabase
        .rpc('create_company_with_admin', {
          p_name: data.name,
          p_slug: data.slug,
          p_admin_email: data.admin_email,
          p_admin_name: data.admin_name,
        });

      if (error) {
        console.error('Error creating company with admin:', error);
        throw error;
      }
      
      return result;
    },
    onSuccess: async (companyId: string) => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      
      // Log audit
      await logAction({
        action: 'company.create',
        entity_type: 'company',
        entity_id: companyId,
        new_values: { id: companyId },
      });
      
      toast({
        title: "Sucesso",
        description: "Empresa criada com sucesso e administrador configurado",
      });
    },
    onError: (error: any) => {
      console.error('Create mutation error:', error);
      toast({
        title: "Erro",
        description: error.message || "Erro ao criar empresa",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...company }: { id: string } & Partial<Company>) => {
      const { data, error } = await supabase
        .from('companies')
        .update(company)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      
      // Log audit
      const oldCompany = companies?.find(c => c.id === variables.id);
      await logAction({
        action: 'company.update',
        entity_type: 'company',
        entity_id: variables.id,
        old_values: oldCompany,
        new_values: data,
        company_id: variables.id
      });
    },
  });

  const handleSave = async (data: any) => {
    if (selectedCompany) {
      // Editing existing company - use update mutation
      const { admin_email, admin_name, slug, ...companyData } = data;
      await updateMutation.mutateAsync({
        id: selectedCompany.id,
        ...companyData,
      });
    } else {
      // Creating new company - use create mutation with admin
      await createMutation.mutateAsync(data as CompanyWithAdmin);
    }
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setDetailModalOpen(true);
  };

  const handleQuickEdit = (company: Company) => {
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedCompany(null);
    setModalOpen(true);
  };

  const handleStatusChange = async (company: Company, newStatus: string) => {
    try {
      await updateMutation.mutateAsync({ id: company.id, status: newStatus });
      
      // Log audit
      await logAction({
        action: 'company.status_change',
        entity_type: 'company',
        entity_id: company.id,
        old_values: { status: company.status },
        new_values: { status: newStatus },
        company_id: company.id
      });
      
      toast({
        title: 'Sucesso',
        description: `Status da empresa alterado para ${newStatus}`,
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao alterar status da empresa',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteCompany = (company: Company) => {
    setSelectedCompany(company);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (companyId: string, confirmationName: string, cancelSubscriptions?: boolean) => {
    await deleteCompany(companyId, confirmationName, cancelSubscriptions);
  };

  const statusColors = {
    active: 'bg-success/10 text-success',
    trial: 'bg-warning/10 text-warning',
    suspended: 'bg-destructive/10 text-destructive',
  };

  const statusLabels = {
    active: 'Ativo',
    trial: 'Trial',
    suspended: 'Suspenso',
  };

  const columns: ColumnDef<Company>[] = [
    {
      accessorKey: 'name',
      header: 'Nome',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.getValue('name')}</span>
        </div>
      ),
    },
    {
      accessorKey: 'hasActiveTrial',
      header: 'Trial',
      cell: ({ row }) => {
        const hasActiveTrial = row.original.hasActiveTrial;
        const daysRemaining = row.original.trialDaysRemaining;
        
        if (!hasActiveTrial || daysRemaining === null) {
          return <span className="text-muted-foreground text-xs">-</span>;
        }
        
        // Handle expired trials
        if (daysRemaining <= 0) {
          return (
            <Badge 
              variant="outline" 
              className="font-mono text-xs bg-destructive/10 text-destructive border-destructive/20"
            >
              <Clock className="mr-1 h-3 w-3" />
              Expirado
            </Badge>
          );
        }
        
        // Colors based on remaining days
        const getBadgeColor = (days: number) => {
          if (days <= 3) return 'bg-destructive/10 text-destructive border-destructive/20';
          if (days <= 7) return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
          return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
        };
        
        return (
          <Badge 
            variant="outline" 
            className={cn(
              'font-mono text-xs',
              getBadgeColor(daysRemaining)
            )}
          >
            <Clock className="mr-1 h-3 w-3" />
            {daysRemaining}d restante{daysRemaining !== 1 ? 's' : ''}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'doc_cnpj',
      header: 'CNPJ',
      cell: ({ row }) => {
        const cnpj = row.getValue('doc_cnpj') as string;
        return cnpj || <span className="text-muted-foreground">-</span>;
      },
    },
    {
      accessorKey: 'timezone',
      header: 'Fuso Horário',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as keyof typeof statusLabels;
        return (
          <Badge className={statusColors[status] || 'bg-muted'}>
            {statusLabels[status] || status}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Criada em',
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'));
        return date.toLocaleDateString('pt-BR');
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const company = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleEdit(company)}>
                Ver Detalhes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleQuickEdit(company)}>
                Edição Rápida
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {company.status !== 'active' && (
                <DropdownMenuItem 
                  onClick={() => handleStatusChange(company, 'active')}
                  className="text-success"
                >
                  Ativar
                </DropdownMenuItem>
              )}
              {company.status !== 'suspended' && (
                <DropdownMenuItem 
                  onClick={() => handleStatusChange(company, 'suspended')}
                  className="text-destructive"
                >
                  Suspender
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleDeleteCompany(company)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir Empresa
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Empresas</h1>
          <p className="text-muted-foreground">
            Gerencie as empresas cadastradas na plataforma
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Empresa
        </Button>
      </div>

      <div className="bg-card rounded-lg border">
        <div className="p-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex space-x-4 animate-pulse">
                  <div className="h-4 bg-muted rounded w-32"></div>
                  <div className="h-4 bg-muted rounded w-24"></div>
                  <div className="h-4 bg-muted rounded w-20"></div>
                  <div className="h-4 bg-muted rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={companies || []}
              searchKey="name"
              searchPlaceholder="Buscar empresas..."
            />
          )}
        </div>
      </div>

      <CompanyModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        company={selectedCompany}
        onSave={handleSave}
      />

      <CompanyDetailModal
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        company={selectedCompany}
        onSave={handleSave}
      />

      <DeleteCompanyModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        company={selectedCompany}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default CompaniesPage;